import type { Assessment } from "../assessment-types";

export const NERVOUS_SYSTEM_ASSESSMENT: Assessment = {
  slug: "nervous-system",
  emoji: "🌿",
  title: "Nervous-System State",
  tagline: "Where you spend most of your day",
  intro:
    "This reflects on which nervous-system states you tend to spend time in — alert and on edge, shut down and distant, or settled and at ease. Noticing this pattern is often the first step towards more steadiness.",
  questions: [
    { id: "q1", text: "Do you feel on edge or alert even when nothing is actually wrong?" },
    { id: "q2", text: "Do you startle easily at small noises or sudden movement?" },
    { id: "q3", text: "Do you find it hard to relax, even when you have the chance to?" },
    { id: "q4", text: "Do you feel tired but wired, like you can't switch off?" },
    { id: "q5", text: "Do you notice yourself going flat or foggy when things feel like too much?" },
    { id: "q6", text: "Do you feel disconnected from your body for stretches of the day?" },
    { id: "q7", text: "Do you find yourself scanning a room or situation for signs of danger?" },
    { id: "q8", text: "Do you feel low energy or heavy without an obvious reason?" },
    { id: "q9", text: "Does it take you a long time to feel calm after something stressful happens?" },
    { id: "q10", text: "Do you notice tension in your shoulders, jaw or stomach through the day?" },
    { id: "q11", text: "Do ordinary demands (emails, chores, plans) feel more overwhelming than they seem to for others?" },
    { id: "q12", text: "Do you find it hard to tell when you're actually safe versus when you're not?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "You seem to spend a good amount of time in a settled, steady state.",
      body: "This suggests your nervous system generally has room to rest and recover. It's still worth checking in with yourself on harder days.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "You may recognise some patterns of feeling alert or flat more than you'd like.",
      body: "Most people move between states like this sometimes, especially under stress. Simple practices that support the body, like slow breathing or movement, can help nudge things back towards ease.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "You may recognise a pattern of spending a lot of your day feeling on edge or shut down.",
      body: "This often develops when the nervous system has had to stay ready for threat, or has learned that shutting down is safer. It's an adaptation, and one that can gradually soften with consistent, gentle care.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "You may recognise a strong pattern of your nervous system staying in alert or shutdown states.",
      body: "Living this way is genuinely tiring, and it usually reflects a history where safety wasn't reliable. This is not a personal failing, it's how your body adapted to get through. Small, repeated moments of safety can help widen your capacity to feel settled.",
    },
  ],
  next: [
    { label: "The polyvagal ladder explained", to: "/learn/polyvagal-ladder" },
    { label: "Understanding trauma responses", to: "/learn/trauma-responses" },
    { label: "Try a somatic practice", to: "/somatic" },
    { label: "Try a grounding exercise", to: "/grounding" },
  ],
};
